import React, { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";

// Material UI
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  IconButton,
  LinearProgress,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Tooltip,
  Snackbar,
  Alert,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import CommonComponents from "./CommonComponents.js";

const NewPost = ({
  showDialog,
  setShowDialog = () => {},
  repairId = null,
  amenityId = null,
  cleaningId = null, // reserved for future use
}) => {
  // Local UI state
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const [progressBar, setProgressBar] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const [images, setImages] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteIdx, setPendingDeleteIdx] = useState(-1);

  // snackbars (fallback if CommonComponents context isn’t present)
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const url = process.env.REACT_APP_SERVER_URL;
  const { displayNotificationMessage } = useContext(CommonComponents) || {};

  // Responsive masonry cols (unchanged from your current logic)
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const cols = isXs ? 1 : isSm ? 2 : isMd ? 3 : 4;

  // Cleanup preview URL on unmount/change
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const notify = (severity, message) => {
    if (typeof displayNotificationMessage === "function") {
      const map = { success: "success", error: "danger", warning: "warning", info: "info" };
      displayNotificationMessage(map[severity] || "info", message);
    } else {
      setSnack({ open: true, message, severity });
    }
  };

  const getImagesById = useCallback(async () => {
    try {
      let data = [];
      if (repairId) {
        const res = await axios.get(`${url}/images/getImagesByRepairId/${repairId}`);
        data = res.data || [];
      } else if (amenityId) {
        const res = await axios.get(`${url}/images/getImagesByAmenityId/${amenityId}`);
        data = res.data || [];
      } else if (cleaningId) {
        const res = await axios.get(`${url}/images/getImagesByCleaningId/${cleaningId}`);
        data = res.data || [];
      }
      setImages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.log(e);
      notify("error", "Failed to load images");
    }
  }, [amenityId, repairId, cleaningId, url]);

  // Load images when dialog opens
  useEffect(() => {
    if (showDialog) getImagesById();
  }, [showDialog, getImagesById]);

  const handleClose = () => setShowDialog(false);

  const clearSelection = () => {
    setFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setProgressBar(0);
  };

  const handleFilePick = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setProgressBar(0);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (f) setPreviewUrl(URL.createObjectURL(f));
    // allow re-picking same file again later
    e.target.value = "";
  };

  const handleKeyActions = (e) => {
    if (!file) return;
    if (e.key === "Enter") {
      e.preventDefault();
      handleUpload();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      clearSelection();
    }
  };

  const handleUpload = async (event) => {
    event?.preventDefault();
    if (!file) {
      notify("warning", "Please select an image first.");
      return;
    }
    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("image", file);

      let tempImageId = null;

      const res = await axios.post(`${url}/api/posts`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          const pct = Math.round((evt.loaded * 100) / (evt.total || 1));
          setProgressBar(pct);
        },
      });

      const parsed = res?.data; // { data: <imageId> } or { imageId: <id> }
      tempImageId = parsed?.data ?? parsed?.imageId ?? null;
      if (!tempImageId) throw new Error("Upload succeeded but imageId is missing from response.");

      // Create relation based on context
      if (repairId) {
        await axios.post(`${url}/addRepairImageRelation`, { imageId: tempImageId, repairId });
      }
      if (amenityId) {
        await axios.post(`${url}/addAmenityImageRelation`, { imageId: tempImageId, amenityId });
      }
      if (cleaningId) {
        await axios.post(`${url}/addCleaningImageRelation`, { imageId: tempImageId, cleaningId }); // optional
      }

      notify("success", "Image uploaded");

      // Reset upload UI
      clearSelection();

      // Refresh images
      await getImagesById();
    } catch (e) {
      console.log(e);
      notify("error", "Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  const requestDelete = (idx) => {
    setPendingDeleteIdx(idx);
    setConfirmOpen(true);
  };
  const cancelDelete = () => {
    setConfirmOpen(false);
    setPendingDeleteIdx(-1);
  };
  const confirmDelete = async () => {
    const idx = pendingDeleteIdx;
    setConfirmOpen(false);
    setPendingDeleteIdx(-1);
    const renderedReversed = [...images].reverse(); // newest first
    const image = renderedReversed[idx];
    if (!image) return;

    try {
      if (repairId) {
        await axios.delete(`${url}/images/deleteRepairImageRelation/${image.id}`);
      }
      if (amenityId) {
        await axios.delete(`${url}/images/deleteAmenityImageRelation/${image.id}`);
      }
      if (cleaningId) {
        await axios.delete(`${url}/images/deleteCleaningImageRelation/${image.id}`); // optional
      }
      await axios.delete(`${url}/images/deleteImageDetailsByImageId/${image.id}`);

      notify("success", "Image deleted");
      await getImagesById();
    } catch (e) {
      console.log(e);
      notify("error", "Failed to delete image");
    }
  };

  const openInNew = (imageUrl) => {
    if (imageUrl) window.open(imageUrl, "_blank", "noopener,noreferrer");
  };

  // Build the list to render: first tile = uploader, then reversed images
  const imagesReversed = [...images].reverse();

  return (
    <>
      <Dialog
        open={showDialog}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ px: 3, py: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography variant="h6" fontWeight={700}>
              Images
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <ImageList variant="masonry" cols={cols} gap={12}>
            {/* 1) Upload tile */}
            <ImageListItem key="__upload_tile__">
              <Card
                variant="outlined"
                onKeyDown={handleKeyActions}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  position: "relative",
                  outline: "none",
                }}
                tabIndex={0} // allow keyboard focus for Enter/Esc
              >
                <CardContent sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    {/* <Box
                      sx={{
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 2,
                        minHeight: 220,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        p: 2,
                        bgcolor: (t) => (!previewUrl ? t.palette.grey[100] : "transparent"),
                      }}
                    > */}
                      {previewUrl ? (
                        <Box
                          component="img"
                          src={previewUrl}
                          alt="Preview"
                          sx={{ maxWidth: "100%", maxHeight: 180, objectFit: "contain" }}
                        />
                      ) : (
                        <Stack alignItems="center" spacing={1} sx={{ py: 3 }}>
                          <CloudUploadOutlinedIcon fontSize="large" />
                          <Typography variant="body1" sx={{ opacity: 0.8 }}>
                            Select an image to upload
                          </Typography>
                          <Button
                            variant="contained"
                            component="label"
                            disableElevation
                            sx={{ borderRadius: 2, mt: 1 }}
                          >
                            Choose File
                            <input type="file" accept="image/*" capture="environment" hidden onChange={handleFilePick} />
                          </Button>
                        </Stack>
                        
                      )}
                    {/* </Box> */}

                    {/* Actions when a file is selected */}
                    {file && (
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        sx={{ justifyContent: "space-between" }}
                      >
                        <Stack direction="row" spacing={1.5}>
                          <Button
                            variant="contained"
                            component="label"
                            disableElevation
                            sx={{ borderRadius: 2 }}
                          >
                            Replace
                            <input type="file" accept="image/*" hidden onChange={handleFilePick} />
                          </Button>
                          {/* <Button
                            variant="outlined"
                            onClick={clearSelection}
                            sx={{ borderRadius: 2 }}
                          >
                            Cancel
                          </Button> */}
                        </Stack>
                        <Button
                          variant="contained"
                          disableElevation
                          onClick={handleUpload}
                          disabled={isUploading}
                          sx={{ borderRadius: 2 }}
                        >
                          Upload
                        </Button>
                      </Stack>
                    )}

                    {/* Progress */}
                    {isUploading && (
                      <Box>
                        <LinearProgress variant="determinate" value={progressBar} />
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mt: 0.5, textAlign: "right" }}
                        >
                          {progressBar}%
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </ImageListItem>

            {/* 2) Existing images (reversed) */}
            {imagesReversed.length === 0 ? (
              <Typography key="__empty__" variant="body1" sx={{ opacity: 0.8 }}>
                No images yet.
              </Typography>
            ) : (
              imagesReversed.map((img, idx) => (
                <ImageListItem key={img.id || img.imageUrl || idx}>
                  <Card
                    elevation={0}
                    sx={{
                      position: "relative",
                      borderRadius: 3,
                      overflow: "hidden",
                      bgcolor: "background.paper",
                      "&:hover .hoverbar": { opacity: 1 },
                    }}
                  >
                    <CardActionArea onClick={() => openInNew(img.imageUrl)}>
                      <CardMedia
                        component="img"
                        src={img.imageUrl}
                        alt={img.caption || `Image ${idx + 1}`}
                        sx={{ display: "block" }}
                        loading="lazy"
                      />
                    </CardActionArea>

                    <ImageListItemBar
                      title={img.caption || ""}
                      subtitle={img.createdAt ? new Date(img.createdAt).toLocaleString() : ""}
                      className="hoverbar"
                      actionIcon={
                        <Stack direction="row" spacing={1} sx={{ pr: 1.5 }}>
                          <Tooltip title="Open in new tab">
                            <IconButton
                              onClick={() => openInNew(img.imageUrl)}
                              sx={{
                                bgcolor: "rgba(255,255,255,0.85)",
                                "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                              }}
                              size="small"
                            >
                              <OpenInNewRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={() => requestDelete(idx)}
                              sx={{
                                bgcolor: "rgba(255,255,255,0.85)",
                                "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                              }}
                              size="small"
                            >
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      }
                      sx={{
                        transition: "opacity 0.2s ease",
                        opacity: 0,
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)",
                        ".MuiImageListItemBar-title, .MuiImageListItemBar-subtitle": {
                          color: "#fff",
                          textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                        },
                      }}
                      position="bottom"
                    />
                  </Card>
                </ImageListItem>
              ))
            )}
          </ImageList>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleClose} variant="contained" disableElevation sx={{ borderRadius: 2 }}>
              Done
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={confirmOpen} onClose={cancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Delete image?</DialogTitle>
        <DialogContent>
          <Typography variant="body3" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar (fallback if CommonComponents isn’t wired) */}
      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NewPost;
